import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getDuelById, startDuel } from '../api/duels';
import { UserContext } from '../context/UserContext';
import { useQuickDuel } from '../context/QuickDuelContext';
import { isWebSocketConnected, subscribeToDuel } from '../api/websocket';
import HrnbLoader from '../components/controls/HrnbLoader';
import HrnbAlert from '../components/controls/HrnbAlert';
import HrnbButton from '../components/controls/HrnbButton';
import { HrnbTabs, HrnbTab } from '../components/controls/HrnbTabs';
import DuelVoting from '../components/duels/DuelVoting';
import DuelVotingResults from '../components/duels/DuelVotingResults';
import DuelJudgeVerdicts from '../components/duels/DuelJudgeVerdicts';
import DuelSpectatorComments from '../components/duels/DuelSpectatorComments';
import WorkUploader from '../components/duels/WorkUploader';
import HrnbCard from '../components/controls/HrnbCard';
import HrnbModal from '../components/controls/HrnbModal';
import '../css/duels/DuelDetail.css';

/**
 * Страница деталей быстрой дуэли
 * Показывает всю информацию о дуэли: раунды, участники, судьи, работы, голосование
 */
function QuickDuelDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { currentUser } = useContext(UserContext);
    const { clearMatch } = useQuickDuel();

    const [duel, setDuel] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('info');
    const [isJudge, setIsJudge] = useState(false);
    const [showWorkUploader, setShowWorkUploader] = useState(false);
    const [selectedRound, setSelectedRound] = useState(null);
    const [isStarted, setIsStarted] = useState(false);

    // Загрузка деталей дуэли
    useEffect(() => {
        const loadDuel = async () => {
            setLoading(true);
            setError(null);

            try {
                const data = await getDuelById(id);
                setDuel(data);
                setIsStarted(data.status === 'IN_PROGRESS' || data.status === 'FINISHED');

                // Проверка, является ли пользователь судьёй
                if (data.judges && currentUser) {
                    const judge = data.judges.find(j => 
                        j.id === currentUser.id || j.profileId === currentUser.id
                    );
                    setIsJudge(!!judge);
                }
            } catch (err) {
                console.error('Ошибка при загрузке дуэли:', err);
                if (err.response?.status === 401 || err.response?.status === 403) {
                    setError('Для просмотра деталей дуэли необходимо войти в систему');
                } else if (err.response?.status === 404) {
                    setError('Дуэль не найдена. Возможно, она была удалена.');
                } else {
                    setError('Не удалось загрузить дуэль.');
                }
            } finally {
                setLoading(false);
            }
        };

        loadDuel();
    }, [id, currentUser]);

    // WebSocket подписка на обновления дуэли
    useEffect(() => {
        if (!id || !isWebSocketConnected()) {
            return;
        }

        console.log('[QuickDuelDetailPage] Подписка на WebSocket события дуэли:', id);

        const unsubscribe = subscribeToDuel(id, (event) => {
            console.log('[QuickDuelDetailPage] Duel event:', event);

            switch (event?.eventType) {
                case 'DUEL_UPDATED':
                case 'DUEL_STARTED':
                case 'DUEL_FINISHED':
                case 'DUEL_CANCELLED':
                case 'PARTICIPANT_JOINED':
                case 'PARTICIPANT_LEFT':
                case 'ROUND_STARTED':
                case 'ROUND_FINISHED':
                case 'VOTE_SUBMITTED':
                    getDuelById(id).then((updatedDuel) => {
                        setDuel(updatedDuel);
                        setIsStarted(updatedDuel.status === 'IN_PROGRESS' || updatedDuel.status === 'FINISHED');
                    }).catch(console.error);
                    break;
                default:
                    console.log('[QuickDuelDetailPage] Неизвестное событие:', event);
            }
        });

        return () => {
            if (unsubscribe) {
                unsubscribe.unsubscribe();
            }
        };
    }, [id]);

    // Обработчик начала дуэли
    const handleStart = async () => {
        setActionLoading(true);
        try {
            await startDuel(id);
            const updatedDuel = await getDuelById(id);
            setDuel(updatedDuel);
            setIsStarted(true);
        } catch (err) {
            console.error('Ошибка при старте дуэли:', err);
            setError(err.response?.data?.message || 'Не удалось начать дуэль');
        } finally {
            setActionLoading(false);
        }
    };

    // Обработчик открытия загрузчика работы
    const handleUploadWork = (round) => {
        setSelectedRound(round);
        setShowWorkUploader(true);
    };

    // Обработчик успешной загрузки работы
    const handleWorkSuccess = () => {
        setShowWorkUploader(false);
        setSelectedRound(null);
        // Обновляем данные дуэли
        getDuelById(id).then(setDuel).catch(console.error);
    };

    // Статусы
    const statusLabels = {
        PENDING_PARTICIPANTS: 'Ожидание участников',
        READY_TO_START: 'Готово к началу',
        IN_PROGRESS: 'В процессе',
        FINISHED: 'Завершено',
        CANCELLED: 'Отменено'
    };

    const formatLabels = {
        SOLO: '1 на 1',
        TEAM_2V2: '2 на 2',
        TEAM_3V3: '3 на 3'
    };

    const roundTypeLabels = {
        SINGLE_ROUND: '1 раунд',
        BEST_OF_THREE: 'До 3 побед'
    };

    const formatRoundType = (roundType) => {
        const types = {
            AUDIO: 'Аудио',
            TEXT: 'Текст',
            HYBRID: 'Гибрид'
        };
        return types[roundType] || roundType;
    };

    // Форматирование даты
    const formatDate = (dateString) => {
        if (!dateString) return '—';
        return new Date(dateString).toLocaleDateString('ru-RU', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // URL обложки
    const coverUrl = duel?.coverImageId
        ? `${process.env.REACT_APP_API_URL || 'https://api.hrnb.ru/api'}/files/${duel.coverImageId}`
        : null;

    if (loading) {
        return <HrnbLoader />;
    }

    if (error && !duel) {
        return (
            <div className="duel-detail">
                <HrnbAlert variant="danger">{error}</HrnbAlert>
                <div style={{ marginTop: 16 }}>
                    <Link to="/quick-duels">
                        <HrnbButton variant="secondary">Вернуться к списку</HrnbButton>
                    </Link>
                </div>
            </div>
        );
    }

    // Находим текущего участника
    const currentParticipant = duel?.participants?.find(p => 
        p.profileId === currentUser?.id || p.userId === currentUser?.id
    );

    return (
        <div className="duel-detail">
            {error && (
                <HrnbAlert variant="danger" onClose={() => setError(null)}>
                    {error}
                </HrnbAlert>
            )}

            {coverUrl && (
                <div className="duel-detail__cover">
                    <img src={coverUrl} alt={duel.title} className="duel-detail__image" />
                </div>
            )}

            <HrnbTabs value={activeTab} onChange={setActiveTab}>
                <HrnbTab label="Информация" value="info" />
                <HrnbTab label="Раунды" value="rounds" />
                <HrnbTab label="Голосование" value="voting" />
                <HrnbTab label="Вердикты" value="verdicts" />
                <HrnbTab label="Комментарии" value="comments" />
            </HrnbTabs>

            <div className="duel-detail__content">
                {activeTab === 'info' && (
                    <>
                        <div className="duel-detail__header">
                            <div className="duel-detail__badges">
                                <span className="duel-detail__status">
                                    {statusLabels[duel.status] || duel.status}
                                </span>
                                <span className="duel-detail__format">
                                    {formatLabels[duel.format]} • {roundTypeLabels[duel.roundType]}
                                </span>
                                <span className={`duel-detail__ws-status ${isWebSocketConnected() ? 'duel-detail__ws-status--online' : ''}`}>
                                    {isWebSocketConnected() ? '🟢 Онлайн' : '🟡 Оффлайн'}
                                </span>
                            </div>

                            <h1 className="duel-detail__title">{duel.title}</h1>
                        </div>

                        {duel.description && (
                            <p className="duel-detail__description">{duel.description}</p>
                        )}

                        <div className="duel-detail__meta">
                            <div className="duel-detail__meta-item">
                                <span className="duel-detail__meta-label">Создатель:</span>
                                <span className="duel-detail__meta-value">
                                    {duel.creator?.nickname || duel.creator?.name || '—'}
                                </span>
                            </div>

                            <div className="duel-detail__meta-item">
                                <span className="duel-detail__meta-label">Участников:</span>
                                <span className="duel-detail__meta-value">
                                    {duel.participants?.length || 0}
                                </span>
                            </div>

                            <div className="duel-detail__meta-item">
                                <span className="duel-detail__meta-label">Тип контента:</span>
                                <span className="duel-detail__meta-value">
                                    {formatRoundType(duel.roundContentType)}
                                </span>
                            </div>

                            {duel.startedAt && (
                                <div className="duel-detail__meta-item">
                                    <span className="duel-detail__meta-label">Начато:</span>
                                    <span className="duel-detail__meta-value">
                                        {formatDate(duel.startedAt)}
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Участники */}
                        {duel.participants && duel.participants.length > 0 && (
                            <div className="duel-detail__section">
                                <h3 className="duel-detail__section-title">Участники</h3>
                                <div className="duel-detail__participants">
                                    {duel.participants.map((participant, index) => (
                                        <div key={participant.id || index} className="duel-detail__participant">
                                            <span className="duel-detail__participant-name">
                                                {participant.nickname || participant.name}
                                            </span>
                                            {participant.teamNumber && (
                                                <span className="duel-detail__participant-team">
                                                    Команда {participant.teamNumber}
                                                </span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Судьи */}
                        {duel.judges && duel.judges.length > 0 && (
                            <div className="duel-detail__section">
                                <h3 className="duel-detail__section-title">Судьи</h3>
                                <div className="duel-detail__participants">
                                    {duel.judges.map((judge, index) => (
                                        <div key={judge.id || index} className="duel-detail__participant">
                                            <span className="duel-detail__participant-name">
                                                👤 {judge.nickname || judge.name}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Раунды */}
                        {duel.rounds && duel.rounds.length > 0 && (
                            <div className="duel-detail__section">
                                <h3 className="duel-detail__section-title">Раунды</h3>
                                <div className="duel-detail__rounds">
                                    {duel.rounds.map((round, index) => (
                                        <div key={round.id || index} className="duel-detail__round">
                                            <span className="duel-detail__round-title">
                                                Раунд {round.roundNumber || index + 1}
                                                {round.theme && `: ${round.theme}`}
                                            </span>
                                            <span className={`duel-detail__round-status duel-detail__round-status--${round.status?.toLowerCase()}`}>
                                                {round.status || 'Ожидание'}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Действия */}
                        <div className="duel-detail__actions">
                            {!isStarted && currentUser && (
                                <HrnbButton
                                    variant="primary"
                                    onClick={handleStart}
                                    disabled={actionLoading}
                                >
                                    {actionLoading ? 'Запуск...' : '▶️ Начать дуэль'}
                                </HrnbButton>
                            )}
                            <Link to="/quick-duels">
                                <HrnbButton variant="secondary">
                                    Назад к списку
                                </HrnbButton>
                            </Link>
                        </div>
                    </>
                )}

                {activeTab === 'rounds' && (
                    <div className="duel-detail__rounds-tab">
                        <HrnbCard title="Раунды дуэли">
                            {duel.rounds && duel.rounds.length > 0 ? (
                                duel.rounds.map((round, index) => (
                                    <div key={round.id} className="round-section">
                                        <h3 className="round-section__title">
                                            Раунд {round.roundNumber || index + 1}
                                            <span className="round-section__status">
                                                {round.status}
                                            </span>
                                        </h3>

                                        {round.theme && (
                                            <p className="round-section__theme">Тема: {round.theme}</p>
                                        )}
                                        {round.task && (
                                            <p className="round-section__task">Задание: {round.task}</p>
                                        )}

                                        {/* Кнопка загрузки работы для участника */}
                                        {currentParticipant && round.status === 'OPEN' && (
                                            <HrnbButton
                                                variant="primary"
                                                onClick={() => handleUploadWork(round)}
                                                className="mt-3"
                                            >
                                                📤 Загрузить работу
                                            </HrnbButton>
                                        )}

                                        {/* Список работ раунда */}
                                        {round.works && round.works.length > 0 && (
                                            <div className="round-section__works">
                                                <h4>Работы:</h4>
                                                {round.works.map((work, workIndex) => (
                                                    <div key={work.id} className="work-item">
                                                        <span className="work-item__author">
                                                            {work.authorName || `Участник ${workIndex + 1}`}
                                                        </span>
                                                        {work.track && (
                                                            <audio controls className="work-item__audio">
                                                                <source src={`${process.env.REACT_APP_API_URL || 'https://api.hrnb.ru/api'}/files/${work.track.fileId}`} />
                                                                Ваш браузер не поддерживает аудио
                                                            </audio>
                                                        )}
                                                        {work.text && (
                                                            <div className="work-item__text">
                                                                <pre>{work.text.content}</pre>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))
                            ) : (
                                <HrnbAlert variant="info">Раунды ещё не созданы</HrnbAlert>
                            )}
                        </HrnbCard>

                        {/* Модальное окно загрузки работы */}
                        {showWorkUploader && selectedRound && currentParticipant && (
                            <HrnbModal
                                isOpen={showWorkUploader}
                                onClose={() => {
                                    setShowWorkUploader(false);
                                    setSelectedRound(null);
                                }}
                                title="Загрузка работы"
                                size="lg"
                            >
                                <div className="work-uploader-modal">
                                    <p>Раунд {selectedRound.roundNumber || selectedRound.id}</p>
                                    <WorkUploader
                                        duelId={id}
                                        roundId={selectedRound.id}
                                        participantId={currentParticipant.id}
                                        authorId={currentUser.id}
                                        duelType={duel.roundContentType?.toLowerCase() || 'hybrid'}
                                        onSuccess={handleWorkSuccess}
                                        onCancel={() => {
                                            setShowWorkUploader(false);
                                            setSelectedRound(null);
                                        }}
                                    />
                                </div>
                            </HrnbModal>
                        )}
                    </div>
                )}

                {activeTab === 'voting' && (
                    <div className="duel-detail__voting">
                        {duel.rounds && duel.rounds.length > 0 ? (
                            duel.rounds.map((round) => (
                                <React.Fragment key={round.id}>
                                    <DuelVoting
                                        duelId={id}
                                        round={round}
                                        currentUser={currentUser}
                                        onVoteUpdate={() => getDuelById(id).then(setDuel).catch(console.error)}
                                    />
                                    <DuelVotingResults
                                        duelId={id}
                                        round={round}
                                    />
                                </React.Fragment>
                            ))
                        ) : (
                            <HrnbAlert variant="info">Голосование ещё не доступно</HrnbAlert>
                        )}
                    </div>
                )}

                {activeTab === 'verdicts' && (
                    <div className="duel-detail__verdicts">
                        {duel.rounds && duel.rounds.length > 0 ? (
                            duel.rounds.map((round) => (
                                <div key={round.id}>
                                    <h3>Раунд {round.roundNumber || round.id}</h3>
                                    <DuelJudgeVerdicts
                                        duelId={id}
                                        roundId={round.id}
                                        isJudge={isJudge}
                                    />
                                </div>
                            ))
                        ) : (
                            <HrnbAlert variant="info">Вердикты ещё не доступны</HrnbAlert>
                        )}
                    </div>
                )}

                {activeTab === 'comments' && (
                    <div className="duel-detail__comments">
                        <DuelSpectatorComments duelId={id} />
                    </div>
                )}
            </div>
        </div>
    );
}

export default QuickDuelDetailPage;
